import { Link } from 'react-router-dom';

const LAST_UPDATED = 'July 20, 2026';

const Section = ({ id, title, children }) => (
  <section id={id} className="mb-10">
    <h2 className="text-2xl font-semibold text-dark-slate mb-3">{title}</h2>
    <div className="space-y-3 text-slate-gray leading-relaxed">{children}</div>
  </section>
);

const Terms = () => {
  return (
    <div className="min-h-screen bg-frost-white">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-xl font-bold text-dark-slate">GradLink</Link>
          <nav className="flex gap-6 text-sm text-slate-gray">
            <Link to="/login" className="hover:text-dark-slate">Sign in</Link>
            <Link to="/privacy" className="hover:text-dark-slate">Privacy</Link>
          </nav>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10">
        <h1 className="text-4xl font-bold text-dark-slate mb-2">Terms of Service</h1>
        <p className="text-sm text-slate-gray mb-10">Last updated: {LAST_UPDATED}</p>

        <p className="text-slate-gray leading-relaxed mb-10">
          These Terms of Service ("Terms") govern your access to and use of the
          GradLink website, mobile application, and related services (collectively,
          the "Service"), operated by GradLink ("GradLink," "we," "us," or "our"). By
          creating an account or otherwise using the Service, you agree to these
          Terms and to our{' '}
          <Link to="/privacy" className="text-dark-slate underline">Privacy Policy</Link>.
          If you do not agree, do not use the Service.
        </p>

        <Section id="eligibility" title="1. Eligibility">
          <p>You must be at least 18 years old to use the Service. By using it you
          represent that you are 18 or older, have the legal capacity to enter these
          Terms, and are not barred from using the Service under applicable law.</p>
        </Section>

        <Section id="accounts" title="2. Your account">
          <p>You are responsible for providing accurate account information and for
          keeping your credentials secure. You are responsible for all activity that
          occurs under your account. Notify us at{' '}
            <a href="mailto:hello@gradlink.co" className="text-dark-slate underline">
              hello@gradlink.co
            </a>{' '}
            if you believe your account has been compromised.</p>
          <p>You may only maintain one active personal account. You may not sell,
          transfer, or share your account with anyone else.</p>
          <p>GradLink is a network for alumni and members of educational communities.
          We do not independently verify each user's alumni status, and inclusion of
          any user on the platform is not an endorsement of that person by GradLink or
          by their institution.</p>
        </Section>

        <Section id="acceptable-use" title="3. Acceptable use">
          <p>You agree not to:</p>
          <ul className="list-disc list-outside pl-6 space-y-1">
            <li>Post content that is illegal, defamatory, harassing, hateful, sexually
              explicit, or that violates another person's rights.</li>
            <li>Impersonate any person or entity or misrepresent your affiliation with
              a person or entity.</li>
            <li>Use the Service to spam, phish, distribute malware, or otherwise
              attempt to harm other users.</li>
            <li>Scrape, crawl, harvest, or copy data or content from the Service by
              automated means without our written permission.</li>
            <li>Interfere with or disrupt the Service, its servers, or its networks,
              or attempt to gain unauthorized access to any part of the Service.</li>
            <li>Use the Service to promote or facilitate any illegal activity or to
              violate any applicable law or regulation.</li>
            <li>Use the Service in any manner that could disable, overburden, or
              impair it.</li>
          </ul>
          <p>We may remove content, suspend or terminate accounts, and take other
          action if we believe these Terms have been violated.</p>
        </Section>

        <Section id="user-content" title="4. Your content">
          <p><strong>You own your content.</strong> You retain ownership of the
          content you post to the Service (your "User Content").</p>
          <p><strong>License to us.</strong> You grant GradLink a worldwide,
          non-exclusive, royalty-free, sublicensable license to host, store, cache,
          reproduce, publish, publicly display, publicly perform, adapt, modify (for
          example, to resize an image), translate, and distribute your User Content,
          solely for the purposes of operating, providing, promoting, and improving
          the Service. This license ends when you delete the content or your account,
          except for content already shared with others.</p>
          <p><strong>You are responsible.</strong> You represent that you have all
          rights necessary to grant this license, and that your User Content does
          not violate any law or any third party's rights.</p>
          <p><strong>Feedback.</strong> If you send us suggestions or feedback, we
          may use them without any obligation to you.</p>
        </Section>

        <Section id="payments" title="5. Payments, subscriptions, and refunds">
          <p>Some features (including partner subscriptions, marketplace purchases,
          consultation bookings, and donations) require payment. Payments are
          processed by Stripe. By purchasing you agree to the amount presented at
          checkout and to Stripe's terms.</p>
          <p>Subscription plans renew automatically at the interval indicated at
          checkout unless canceled before the next renewal date. You can cancel a
          subscription from the account or billing area of the Service. Cancelation
          takes effect at the end of the current paid period.</p>
          <p>Except where required by law, payments are non-refundable. If you
          believe you have been charged in error, contact us at{' '}
            <a href="mailto:hello@gradlink.co" className="text-dark-slate underline">
              hello@gradlink.co
            </a>
            .</p>
        </Section>

        <Section id="third-party" title="6. Third-party links and content">
          <p>The Service may display or link to content, listings, jobs, or services
          provided by third parties (including job aggregators such as RemoteOK).
          GradLink does not control and is not responsible for third-party content or
          websites. Your interactions with third parties are between you and them.</p>
        </Section>

        <Section id="ai-features" title="7. AI-assisted features">
          <p>GradLink offers optional AI-assisted features, including content
          moderation, mentor matching, resume review, resume reformatting, and
          resume generation. These features are provided using Anthropic's Claude
          models. AI-generated output can be inaccurate, incomplete, or biased. You
          are responsible for reviewing and verifying any AI-generated output
          before relying on it (for example, before submitting an AI-drafted resume
          to an employer).</p>
        </Section>

        <Section id="dating-safety" title="8. Dating and in-person meetings">
          <p>GradLink offers optional dating and mentorship features. Your safety is
          your responsibility. GradLink does not conduct criminal-background checks
          or otherwise verify the character of users. Exercise caution when
          interacting with people you have met on the Service, whether online or in
          person.</p>
        </Section>

        <Section id="termination" title="9. Termination">
          <p>You may stop using the Service at any time and may request account
          deletion by emailing{' '}
            <a href="mailto:hello@gradlink.co" className="text-dark-slate underline">
              hello@gradlink.co
            </a>
            . We may suspend or terminate your access to the Service at any time,
            with or without notice, if we believe you have violated these Terms, if
            required by law, or to protect GradLink or its users.</p>
          <p>Provisions that by their nature should survive termination will do so,
          including ownership provisions, warranty disclaimers, indemnity, and
          limitations of liability.</p>
        </Section>

        <Section id="disclaimers" title="10. Disclaimers">
          <p>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE," WITHOUT WARRANTIES
          OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING IMPLIED WARRANTIES OF
          MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND
          NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE
          UNINTERRUPTED, SECURE, OR ERROR-FREE, THAT DEFECTS WILL BE CORRECTED, OR
          THAT ANY CONTENT IS ACCURATE OR RELIABLE.</p>
        </Section>

        <Section id="liability" title="11. Limitation of liability">
          <p>TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, GRADLINK AND ITS
          OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS WILL NOT BE LIABLE FOR ANY
          INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY
          LOSS OF PROFITS, REVENUE, DATA, OR GOODWILL, ARISING OUT OF OR IN
          CONNECTION WITH THE SERVICE. OUR TOTAL LIABILITY FOR ANY CLAIM ARISING
          FROM OR RELATED TO THE SERVICE IS LIMITED TO THE GREATER OF (A) THE
          AMOUNT YOU PAID US IN THE TWELVE MONTHS BEFORE THE CLAIM AROSE OR (B)
          ONE HUNDRED U.S. DOLLARS ($100).</p>
          <p>Some jurisdictions do not allow the exclusion of certain warranties or
          the limitation of certain damages, so some of the above limitations may
          not apply to you.</p>
        </Section>

        <Section id="indemnity" title="12. Indemnification">
          <p>You agree to defend, indemnify, and hold harmless GradLink and its
          officers, directors, employees, and agents from any claim, liability,
          damage, loss, or expense (including reasonable attorneys' fees) arising out
          of or related to your use of the Service, your User Content, or your
          violation of these Terms.</p>
        </Section>

        <Section id="governing-law" title="13. Governing law and disputes">
          <p>These Terms are governed by the laws of the State of Louisiana and
          the United States, without regard to conflict-of-laws principles. You
          agree that the state and federal courts located in Louisiana have
          exclusive jurisdiction over any dispute arising out of or related to
          these Terms or the Service, except that either party may seek
          injunctive relief in any court of competent jurisdiction to protect its
          intellectual property or confidential information.</p>
        </Section>

        <Section id="changes" title="14. Changes to these Terms">
          <p>We may update these Terms from time to time. When we make material
          changes we will update the "Last updated" date above and, where
          appropriate, notify you in the app or by email. Your continued use of the
          Service after an update means you accept the updated Terms.</p>
        </Section>

        <Section id="contact" title="15. Contact us">
          <p>Questions about these Terms? Email us at{' '}
            <a href="mailto:hello@gradlink.co" className="text-dark-slate underline">
              hello@gradlink.co
            </a>
            .</p>
        </Section>

        <footer className="border-t border-slate-200 pt-6 mt-16 text-sm text-slate-gray flex flex-wrap gap-4 justify-between">
          <span>&copy; {new Date().getFullYear()} GradLink</span>
          <div className="flex gap-4">
            <Link to="/privacy" className="hover:text-dark-slate">Privacy Policy</Link>
            <Link to="/login" className="hover:text-dark-slate">Sign in</Link>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default Terms;
